import { apiCall } from '@/lib/api/api-helpers'
import type { SavedNode } from '@/components/canvas/utils/coord-converter'
import type { SerializedEdge } from '@/lib/api/canvas-types'

/** API error code the page PATCH answers with when the client's revision is stale */
export const PAGE_REVISION_CONFLICT = 'PAGE_REVISION_CONFLICT'

/** A page's content as the server currently holds it */
export interface RemotePageContent {
  revision: number
  nodes: SavedNode[]
  edges: SerializedEdge[]
}

/**
 * Re-read a page after the server refused a save as stale.
 *
 * The 409 only says "you are behind"; this is the read that says what the page
 * looks like now, which is what the local content has to be merged against.
 *
 * `source=canonical` is not optional here. The default read prefers the
 * normalized canvas_nodes/canvas_edges tables, which a fire-and-forget
 * dual-write fills after the JSONB save has already returned — so it can answer
 * with content older than the revision printed beside it. Merging against that
 * and saving the result would destroy the very work the 409 protected.
 */
export async function fetchRemotePageContent(pageUuid: string): Promise<RemotePageContent | null> {
  const result = (await apiCall(`/api/canvas/pages/${pageUuid}?source=canonical`, true)) as {
    success: boolean
    data?: { revision?: number; nodes?: unknown; edges?: unknown }
  }
  if (!result.success || !result.data) return null

  return {
    revision: result.data.revision ?? 0,
    nodes: Array.isArray(result.data.nodes) ? (result.data.nodes as SavedNode[]) : [],
    edges: Array.isArray(result.data.edges) ? (result.data.edges as SerializedEdge[]) : [],
  }
}
